/* eslint-disable @typescript-eslint/no-explicit-any */
// Need to use the React-specific entry point to allow generating React hooks

import { getQueryStringFromObject } from "@/utils/helper";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseQueryWithDecryption } from "../baseQueryWithEncryption";

export const RoleApi = createApi({
  reducerPath: "RoleApi",
  baseQuery: baseQueryWithDecryption(import.meta.env.VITE_COMPANY_API_URL),
  tagTypes: ["users", "apiKeys", "auth-roles", "themes"],
  endpoints: (builder) => ({
    createAuthRole: builder.mutation({
      query: (payload) => ({
        url: "/roles",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["auth-roles"],
    }),
    updateAuthRole: builder.mutation({
      query: ({ roleCode, body }) => ({
        url: `/roles/${roleCode}`,
        method: "PUT",
        body: body,
      }),
      invalidatesTags: ["auth-roles"],
    }),
    updateAuthRoleById: builder.mutation({
      query: ({ id, body }) => ({
        url: `/roles/${id}`,
        method: "PUT",
        body: body,
      }),
      invalidatesTags: ["auth-roles"],
    }),
    deleteAuthRole: builder.mutation({
      query: (roleCode) => ({
        url: `roles/${roleCode}`,
        method: "DELETE",
      }),
      invalidatesTags: ["auth-roles"],
    }),
    getAuthRoleByCode: builder.query({
      query: ({ roleCode }) => ({
        url: `/roles/${roleCode}`,
        method: "GET",
      }),
      providesTags: ["auth-roles"],
    }),
    getAuthRoleByCodeAuthMgmt: builder.query({
      query: ({ roleCode }) => ({
        url: `/role-mgmt/roles?roleType=${roleCode}`,
        method: "GET",
      }),
      providesTags: ["auth-roles"],
    }),
    getAuthRoleForCompany: builder.query({
      query: (props: any) => {
        const queryStr = getQueryStringFromObject(props);
        return `/role-mgmt/roles?${queryStr}`;
      },
      providesTags: ["auth-roles"],
    }),
    getAuthScreens: builder.query({
      query: (props: any) => {
        const queryStr = getQueryStringFromObject(props);
        return `/auth-screens?${queryStr}`;
      },
    }),
    deleteAuthScreen: builder.mutation({
      query: (id) => ({
        url: `auth-screen/${id}`,
        method: "DELETE",
      }),
    }),
    deleteAuthScreenRole: builder.mutation({
      query: (data) => ({
        url: `auth-screen/${data?.id}?roleCodes=${data?.roles}`,
        method: "DELETE",
      }),
    }),
    getAuthScreensByCode: builder.query({
      query: (body) => ({
        url: `/auth-screens?screenCode=${body?.code}`,
        method: "GET",
      }),
    }),
    createAuthScreen: builder.mutation({
      query: (body) => ({
        url: "/auth-screens",
        method: "POST",
        body,
      }),
    }),
    updateAuthScreen: builder.mutation({
      query: (body) => ({
        url: "/auth-screens",
        method: "PUT",
        body,
      }),
    }),
    getAuthScreenById: builder.query({
      query: (id) => {
        return {
          url: `/auth-screen/${id}`,
          method: "GET",
        };
      },
    }),
    getRoleListing: builder.query({
      query: ({ id }) => {
        return `/screen-mapping/role/list/${id}`;
      },
    }),
    // screens call
    getScreens: builder.query({
      query: () => ({
        url: "/screens",
        method: "GET",
      }),
    }),
    getScreensDetails: builder.query({
      query: ({ id }) => ({
        url: `/screens/${id}`,
        method: "GET",
      }),
    }),
    createScreens: builder.mutation({
      query: (body) => ({
        url: "/screens",
        method: "POST",
        body,
      }),
    }),
    updateScreens: builder.mutation({
      query: ({ id, body }) => ({
        url: `/screens/${id}`,
        method: "PUT",
        body,
      }),
    }),
    deleteScreens: builder.mutation({
      query: (id) => ({
        url: `screens/${id}`,
        method: "DELETE",
      }),
    }),

    //screen mapping
    createScreenRoleMapping: builder.mutation({
      query: (body) => ({
        url: "/screen-mapping",
        method: "POST",
        body,
      }),
    }),

    updateScreenRoleMapping: builder.mutation({
      query: ({ id, body }) => ({
        url: `/screen-mapping/${id}`,
        method: "PUT",
        body,
      }),
    }),
    updateScreenMappingBulkEdit: builder.mutation({
      query: (body) => ({
        url: "/screen-mapping/bulk/update",
        method: "PUT",
        body,
      }),
    }),
    getAuthScreenRoleMappingData: builder.query({
      query: (id) => {
        return {
          url: `/screen-mapping/${id}`,
          method: "GET",
        };
      },
    }),
    getListOfMappedRolesForAScreen: builder.query({
      query: ({ id, size = 10, page = 1, type = "ACTION" }) => {
        return {
          url: `/screen-mapping/list/${id}`,
          method: "GET",
          params: { size, page, type },
        };
      },
    }),
    getListOfHideAndDisableComponents: builder.query({
      query: () => ({
        url: `/auth-hide/disable/list`,
        method: "GET",
      }),
    }),
    // actions call
    getActions: builder.query({
      query: () => ({
        url: "/actions",
        method: "GET",
      }),
    }),
    getActionsDetails: builder.query({
      query: (id) => ({
        url: `/actions/${id}`,
        method: "GET",
      }),
    }),
    createActions: builder.mutation({
      query: (body) => ({
        url: "/actions",
        method: "POST",
        body,
      }),
    }),
    updateActions: builder.mutation({
      query: (body) => ({
        url: `/actions/${body.id}`,
        method: "PUT",
        body,
      }),
    }),
    deleteActions: builder.mutation({
      query: (id) => ({
        url: `actions/${id}`,
        method: "DELETE",
      }),
    }),
    //hide call
    getHideComponent: builder.query({
      query: ({ id }) => ({
        url: `/auth-hide/list/${id}`,
        method: "GET",
      }),
    }),
    createHideComponent: builder.mutation({
      query: (body) => ({
        url: "/auth-hide",
        method: "POST",
        body,
      }),
    }),
    updateHideComponent: builder.mutation({
      query: (body) => ({
        url: `/auth-hide/${body.id}`,
        method: "PUT",
        body,
      }),
    }),
    deleteHideComponent: builder.mutation({
      query: (id) => ({
        url: `auth-hide/${id}`,
        method: "DELETE",
      }),
    }),
    //disable call
    getDisableComponent: builder.query({
      query: ({ id }) => ({
        url: `/disable/list/${id}`,
        method: "GET",
      }),
    }),
    createDisableComponent: builder.mutation({
      query: (body) => ({
        url: "/disable",
        method: "POST",
        body,
      }),
    }),
    updateDisableComponent: builder.mutation({
      query: (body) => ({
        url: `/disable/${body.id}`,
        method: "PUT",
        body,
      }),
    }),
    deleteDisableComponent: builder.mutation({
      query: (id) => ({
        url: `disable/${id}`,
        method: "DELETE",
      }),
    }),

    // themes call
    getThemes: builder.query({
      query: () => ({
        url: "/themes",
        method: "GET",
      }),
      providesTags: ["themes"],
    }),
    getThemeDetails: builder.query({
      query: (id) => ({
        url: `/theme/${id}`,
        method: "GET",
      }),
    }),
    createTheme: builder.mutation({
      query: (body) => ({
        url: "/theme",
        method: "POST",
        body,
      }),
      invalidatesTags: ["themes"],
    }),
    updateTheme: builder.mutation({
      query: ({ id, body }) => ({
        url: `/theme/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["themes"],
    }),
    deleteTheme: builder.mutation({
      query: (id) => ({
        url: `theme/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["themes"],
    }),
  }),
});

export const {
  //roles
  useGetAuthRoleForCompanyQuery,
  useGetAuthScreensQuery,
  useGetAuthScreensByCodeQuery,
  useCreateAuthScreenMutation,
  useCreateAuthRoleMutation,
  useUpdateAuthRoleMutation,
  useUpdateAuthRoleByIdMutation,
  useUpdateAuthScreenMutation,
  useLazyGetAuthRoleByCodeQuery,
  useLazyGetAuthRoleByCodeAuthMgmtQuery,
  useGetAuthScreenByIdQuery,
  useDeleteAuthRoleMutation,
  useDeleteAuthScreenMutation,
  useDeleteAuthScreenRoleMutation,
  useGetRoleListingQuery,
  // screen
  useGetScreensQuery,
  useGetScreensDetailsQuery,
  useCreateScreensMutation,
  useUpdateScreensMutation,
  useDeleteScreensMutation,

  // screen mapping
  useCreateScreenRoleMappingMutation,
  useLazyGetAuthScreenRoleMappingDataQuery,
  useGetListOfMappedRolesForAScreenQuery,
  useUpdateScreenRoleMappingMutation,
  useUpdateScreenMappingBulkEditMutation,
  useGetListOfHideAndDisableComponentsQuery,

  // actions
  useGetActionsDetailsQuery,
  useGetActionsQuery,
  useCreateActionsMutation,
  useDeleteActionsMutation,
  useUpdateActionsMutation,
  //hide
  useGetHideComponentQuery,
  useCreateHideComponentMutation,
  useUpdateHideComponentMutation,
  useDeleteHideComponentMutation,

  //disable
  useGetDisableComponentQuery,
  useCreateDisableComponentMutation,
  useUpdateDisableComponentMutation,
  useDeleteDisableComponentMutation,

  // themes
  useGetThemeDetailsQuery,
  useGetThemesQuery,
  useCreateThemeMutation,
  useUpdateThemeMutation,
  useDeleteThemeMutation,
} = RoleApi;
